import { isOnOrigin } from '@app/shared'
import {
  appKey,
  DEFAULT_PRODUCT,
  PLATFORM_NAME,
  productName,
} from '@app/shared/brand'
import { type ReactNode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { AuthProvider, MAGIC_LINK_PATH, useAuth } from './lib/auth'
import { installChunkReload } from './lib/build'
import { SpaceProvider, useSpace } from './lib/org'
import { useOrgElsewhere } from './lib/own-host'
import { Providers } from './lib/providers'
import {
  currentFace,
  currentProduct,
  currentSite,
  faceFor,
  faceOrigin,
  type OrgPage,
  type PublicRoute,
  siteCarriesTimes,
  siteOrigin,
  usePublicRoute,
} from './lib/public-router'
import { returnPath } from './lib/return-path'
import { AppRouter } from './lib/router'
import { AutopayLinkPage } from './routes/AutopayLinkPage'
import { BoardPage } from './routes/board/BoardPage'
import { CampaignDonatePage } from './routes/CampaignDonatePage'
import { ChangeCardPage } from './routes/ChangeCardPage'
import { ConfirmGiftPage } from './routes/ConfirmGiftPage'
import { ConfirmMembershipPage } from './routes/ConfirmMembershipPage'
import { ConsentPage } from './routes/ConsentPage'
import { ContactPage } from './routes/ContactPage'
import { DemoDoor } from './routes/DemoDoor'
import { DonatePage } from './routes/DonatePage'
import { GiftPage } from './routes/GiftPage'
import { GivingHomePage } from './routes/GivingHomePage'
import { LoginScreen } from './routes/LoginScreen'
import { HomePage } from './routes/marketing/HomePage'
import { OrgsPage } from './routes/marketing/OrgsPage'
import { PlatformHomePage } from './routes/marketing/PlatformHomePage'
import { ShulsPage } from './routes/marketing/ShulsPage'
import { StudioHomePage } from './routes/marketing/StudioHomePage'
import { TerumahHomePage } from './routes/marketing/TerumahHomePage'
import { NoSpaceScreen } from './routes/NoSpaceScreen'
import { PayPage } from './routes/PayPage'
import { PreferencesPage } from './routes/PreferencesPage'
import { PublicZmanimPage } from './routes/PublicZmanimPage'
import { ShulHomePage } from './routes/ShulHomePage'
import { SsoBounce } from './routes/SsoBounce'
import { StopGiftPage } from './routes/StopGiftPage'
import { SharedRecordPage } from './routes/studio/SharedRecordPage'
import { ErrorBoundary } from './ui/ErrorBoundary'
import { PublicNotFound } from './ui/ErrorPage'
import { StaleBuild } from './ui/StaleBuild'
import { ToastProvider } from './ui/Toast'
import { ThemeProvider } from './ui/theme'
import './styles/fonts.css'
import './styles/global.css'
import './ui/ui.css'
import './ui/shell.css'
import './ui/sidebar.css'

/**
 * The landing decision — which SURFACE answers, given who is asking:
 *  - an address that belongs to a product's other face → sent there;
 *  - outside auth entirely (preferences, a donation page) → answered first;
 *  - signed in → the staff app, URL-routed (see `lib/router`);
 *  - asked to sign in → the sign-in screen;
 *  - otherwise → the public surface.
 *
 * WHICH public page is a separate question, and it is not answered here: the
 * route map lives in `lib/public-router`. Keeping the two apart is why this
 * function is readable — a reader asking "what does this URL mean" and one
 * asking "when does the gate apply" no longer have to read the other's answer.
 *
 * A product's public root is its front door, not a congregation's. A visitor
 * who belongs to no org has to have something to read, and the product has to
 * have a page describing itself; one arbitrary org's schedule is neither.
 * Zmanim is a destination, reached deliberately. The app's root is the back
 * office, or the way into it.
 *
 * Nothing here is gated: only the back-office is, and everything the public
 * pages call is a public procedure — no member data is drawn before auth.
 */
function Root() {
  const { me, loading, isPlatformAdmin, signInError, logout } = useAuth()
  const route = usePublicRoute()
  const [showLogin, setShowLogin] = useState(route.kind === 'login')
  const login = () => setShowLogin(true)
  const product = currentProduct()
  const face = currentFace()
  const elsewhere = useOrgElsewhere(route)

  /*
   * Back closes the sign-in screen.
   *
   * It is a STATE over whatever page you were reading rather than a page of its
   * own, so it owns no history entry — and the entry underneath it is the page
   * it is covering. Left standing while the address moves, it would name one
   * page in the bar and show another on the screen, which is the mismatch
   * `backLabel` exists to prevent.
   */
  useEffect(() => {
    const close = () => setShowLogin(false)
    window.addEventListener('popstate', close)
    return () => window.removeEventListener('popstate', close)
  }, [])

  /*
   * A product's two faces never serve each other's pages: an address that
   * belongs to the other face is sent there, path and all. That is what keeps
   * a bookmark into the back office working from the public host, and a shul's
   * page reachable from the app's.
   */
  const belongs = faceFor(route, window.location.pathname)
  if (product && face && belongs && belongs !== face)
    return <Relocate to={faceOrigin(product, belongs)} />

  // An org's pages live on its own product's host, and nothing of it is drawn
  // on another's — not even for the moment the org takes to read.
  if (elsewhere === 'pending') return null
  if (elsewhere) return <Relocate to={elsewhere} />

  // Some of an org's pages stand OUTSIDE auth entirely rather than merely before
  // it, and so are answered ahead of the gate:
  //  - preferences, pay, gift, autopay, stop-gift, confirm-gift and
  //    change-card are token-gated and reached from a message or a statement,
  //    by people who have no login;
  //  - a donation page must work for a visitor who is signed in to a DIFFERENT
  //    org, so deferring to the back office would be wrong, not just unhelpful.
  //
  // Standing outside the gate is not standing outside the ASK: where the way in
  // is a sign-in rather than a link to the app, somebody who has pressed it gets
  // the screen, which is below. Answered ahead of it, the page would return
  // before the request was ever read and the masthead would carry a control
  // that does nothing.
  if (route.kind === 'org' && !showLogin) {
    if (route.page === 'preferences') return <PreferencesPage />
    if (route.page === 'pay') return <PayPage />
    if (route.page === 'gift') {
      const onLogin = me ? undefined : login
      return <GiftPage orgId={route.orgId} onLogin={onLogin} />
    }
    if (route.page === 'autopay') {
      const onLogin = me ? undefined : login
      return <AutopayLinkPage orgId={route.orgId} onLogin={onLogin} />
    }
    if (route.page === 'stop-gift') {
      const onLogin = me ? undefined : login
      return <StopGiftPage orgId={route.orgId} onLogin={onLogin} />
    }
    if (route.page === 'confirm-gift') {
      const onLogin = me ? undefined : login
      return <ConfirmGiftPage orgId={route.orgId} onLogin={onLogin} />
    }
    if (route.page === 'confirm-membership') {
      const onLogin = me ? undefined : login
      return <ConfirmMembershipPage orgId={route.orgId} onLogin={onLogin} />
    }
    if (route.page === 'change-card') {
      const onLogin = me ? undefined : login
      return <ChangeCardPage orgId={route.orgId} onLogin={onLogin} />
    }
    if (route.page === 'donate') {
      /* The masthead is the site's and says the same thing on every page of it,
         so the donation page offers the way in like the rest. Only to somebody
         who has no session, since offering a signed-in visitor a sign-in names
         a state they are not in. A product's public site reads everybody as
         signed out, so there it is always offered, and it leads to the app. */
      const onLogin = me ? undefined : login
      return route.campaignId ? (
        <CampaignDonatePage
          orgId={route.orgId}
          campaignId={route.campaignId}
          onLogin={onLogin}
        />
      ) : (
        <DonatePage orgId={route.orgId} onLogin={onLogin} />
      )
    }
  }

  /*
   * A shared book stands outside auth the way a donation page does, and for a
   * stronger reason: the reader is somebody the owner sent a link to, who very
   * likely has no account at all. Answered ahead of the gate so being signed in
   * to the back office does not take them somewhere else.
   */
  if (route.kind === 'read') return <SharedRecordPage token={route.token} />
  if (route.kind === 'notFound') return <PublicNotFound />

  /*
   * The exchange itself, which only the platform's address serves. Answered
   * before the gate because it IS the gate's answer for another site — and
   * ahead of everything signed-out below, since the whole point is to decide
   * before a sign-in screen is drawn.
   */
  if (route.kind === 'sso' && currentSite() === 'platform')
    return <SsoBounce to={route.to} next={route.next} have={route.have} />

  /*
   * A landing being redeemed is not a page, and nothing may mount over it.
   *
   * `/auth/verify` holds a token and, on a handoff, the address to go on to.
   * The app router answers that path by sending you home — so if it mounts
   * first it rewrites the URL, and the trip ends wherever its own landing is
   * rather than where the link said. Rendering nothing until the redemption has
   * moved the URL leaves exactly one thing deciding where this goes.
   */
  if (window.location.pathname === MAGIC_LINK_PATH) {
    // Unless it failed: then this address is where the person is, and the
    // screen that explains it is also the one that sends another link.
    if (!signInError) return null
    const next = new URLSearchParams(window.location.search).get('next')
    return (
      <LoginScreen
        notice={signInError}
        next={isOnOrigin(next, window.location.origin) ? next : undefined}
      />
    )
  }

  /*
   * The demo door, answered before the gate. A visitor arriving here has no
   * session and wants one — sending them to a sign-in screen first would ask
   * for an address from the one person who has none to give.
   */
  if (route.kind === 'demo' && !me) return <DemoDoor />

  if (loading && !me) return null

  /*
   * Consent belongs to the signed-in person rather than any one space or the
   * platform operator, so it stands outside both gates while requiring auth.
   */
  if (route.kind === 'consent') {
    if (me) return <ConsentPage />
    return <LoginScreen next={returnPath(window.location)} />
  }

  if (me) {
    /*
     * The platform's own address holds one app — the platform surface — and
     * only its operator may enter it. Anybody else signed in is a person whose
     * work is in a product, so they get the front door and its list of them
     * rather than a back office that would refuse every call it made.
     */
    if (currentSite() === 'platform' && !isPlatformAdmin)
      return <PlatformHomePage />
    return (
      <SpaceProvider>
        <RequireSpace email={me.email} onSignOut={logout}>
          <AppRouter />
        </RequireSpace>
      </SpaceProvider>
    )
  }

  /*
   * Signed out on a page that IS the app: ask the platform once whether this
   * person has a session there.
   *
   * Only from an app path — never a marketing home, never a public org page,
   * never a token-gated one, all of which are for people who may have no
   * account at all and must not be sent on a round trip to find out. And only
   * once per tab, so a visitor with no session anywhere sees a sign-in screen
   * rather than a loop between two sites.
   */
  if (shouldAskPlatform(route)) return <AskPlatform />

  // Signed out in the app, with no session anywhere to fetch: the app's own
  // front door is the way in, and a sign-in here stays here.
  if (face === 'app') return <LoginScreen next={returnPath(window.location)} />

  if (showLogin)
    return (
      <LoginScreen
        onBack={() => setShowLogin(false)}
        /* What is behind this screen is what Root would draw instead, so it is
           named here rather than guessed at there. */
        backTo={backLabel(route)}
      />
    )
  if (route.kind === 'org') {
    if (route.page === 'board')
      return <BoardPage orgId={route.orgId} boardId={route.boardId} />
    if (route.page === 'contact')
      return <ContactPage orgId={route.orgId} onLogin={login} />
    if (route.page === '')
      return siteCarriesTimes() ? (
        <ShulHomePage orgId={route.orgId} onLogin={login} />
      ) : (
        <GivingHomePage orgId={route.orgId} onLogin={login} />
      )
    if (!siteCarriesTimes()) return <PublicNotFound />
    return <PublicZmanimPage orgId={route.orgId} onLogin={login} />
  }
  if (route.kind === 'shuls') return <ShulsPage onLogin={login} />
  if (route.kind === 'orgs') return <OrgsPage onLogin={login} />
  /*
   * The root is whoever's front door this is. It is asked LAST among the
   * signed-out answers because a page addressed by its path — an org's, the
   * directory's — is a destination somebody reached deliberately, and the host
   * only decides what is served when nothing else was asked for.
   */
  const site = currentSite()
  if (site === 'platform') return <PlatformHomePage onLogin={login} />
  if (site === 'studio') return <StudioHomePage onLogin={login} />
  if (site === 'terumah') return <TerumahHomePage onLogin={login} />
  return <HomePage onLogin={login} />
}

/**
 * The app, once there is somewhere to be in it.
 *
 * Between the provider and the router because that is the only place the answer
 * is known: the provider asks where this person can work, and every screen below
 * assumes it was told. Rendering nothing while the question is in flight, since
 * "no spaces" and "not asked yet" are the same shape and opposite facts.
 */
function RequireSpace({
  email,
  onSignOut,
  children,
}: {
  email?: string | null
  onSignOut: () => Promise<void>
  children: ReactNode
}) {
  const { spaces, loading } = useSpace()
  /*
   * The platform surface is exempt, and it is not an edge case being waved
   * through: it belongs to no product and works ACROSS spaces, reading each by
   * id rather than being in one. Its operator legitimately belongs to nothing,
   * and requiring a space there would shut them out of the screen that exists
   * to look at everybody else's.
   */
  if (currentSite() === 'platform') return <>{children}</>
  if (loading) return null
  if (spaces.length === 0)
    return <NoSpaceScreen email={email} onSignOut={onSignOut} />
  return <>{children}</>
}

/**
 * Send the browser to the same address on another origin.
 *
 * A replace rather than a push: the address it leaves is not a page anybody
 * was on, so Back should not return to it and be sent on again.
 */
function Relocate({ to }: { to: string }) {
  useEffect(() => {
    const here = window.location
    window.location.replace(`${to}${here.pathname}${here.search}${here.hash}`)
  }, [to])
  return null
}

/**
 * Go and ask the platform whether this person has a session.
 *
 * A component rather than a line in `Root`, because leaving the page is an
 * effect: done while rendering it races React's own second pass, and the two
 * navigations arrive in whichever order the browser felt like.
 *
 * It renders nothing — there is nothing to say in the time a redirect takes,
 * and a spinner between two of them reads as a fault.
 */
function AskPlatform() {
  useEffect(() => {
    const url = new URL('/sso', siteOrigin('platform'))
    url.searchParams.set('to', window.location.origin)
    url.searchParams.set('next', returnPath(window.location))
    sessionStorage.setItem(SSO_ASKED, '1')
    window.location.assign(url.toString())
  }, [])
  return null
}

/**
 * What closing the sign-in screen returns to, in words.
 *
 * It names the page Root would draw instead — a congregation's zmanim, the
 * directory, or whichever front door this host serves. Written as one function
 * beside the branch it mirrors, because a label that names a different page
 * from the one it returns to is worse than no label.
 */
/** What each of an org's pages is called in a sentence. */
const ORG_PAGE_LABELS: Record<OrgPage, string> = {
  zmanim: 'zmanim',
  donate: 'the donation page',
  contact: 'contact',
  preferences: 'your preferences',
  pay: 'payment',
  gift: 'this gift',
  autopay: 'automatic payments',
  'stop-gift': 'your recurring gift',
  'confirm-gift': 'confirming your gift',
  'confirm-membership': 'confirming your membership',
  'change-card': 'the card for your gift',
  board: 'the board',
}

function backLabel(route: PublicRoute): string {
  // The page they were on, not the org's root: the sign-in screen is reachable
  // from any of them, and "back to zmanim" from the donation form names a page
  // the visitor was not looking at. The root itself is not a named page.
  if (route.kind === 'org') {
    if (route.page === '') return 'the congregation'
    return ORG_PAGE_LABELS[route.page]
  }
  if (route.kind === 'shuls') return 'the shuls'
  const site = currentSite()
  return site === 'platform' || site === 'org'
    ? PLATFORM_NAME
    : productName(site)
}

/** Whether this tab has already asked the platform. One question, one tab. */
const SSO_ASKED = appKey('sso', 'asked')

/**
 * Whether to ask the platform for a session before drawing a sign-in screen.
 *
 * The test is what the address IS, not who is looking at it. Only a product's
 * app holds sessions, so only a path into it — its root included — is worth a
 * round trip: it was reached by somebody who was already using it, from a link
 * in a message, a bookmark or the other product. A public site, the platform's
 * own front door and a congregation's domain are for people who may have no
 * account at all, and bouncing them answers nothing.
 *
 * `sso=none` on the URL is the platform saying it has nobody, and it is honored
 * as strictly as the once-per-tab flag: between them there is no arrangement of
 * addresses that bounces twice.
 */
function shouldAskPlatform(route: PublicRoute): boolean {
  if (route.kind !== 'home') return false
  if (currentFace() !== 'app') return false
  if (new URLSearchParams(window.location.search).get('sso') === 'none')
    return false
  return !sessionStorage.getItem(SSO_ASKED)
}

/**
 * What a newer build on the server means for whoever is looking at this one.
 *
 * The test is NOT public-versus-signed-in. It is whether the surface can lose
 * something by reloading, and the donation page settles that: it is public,
 * unauthenticated, and holds a half-entered card. So only the pages that hold
 * nothing at all refresh themselves — the schedule somebody reads on a phone in
 * shul, and the marketing pages. Everywhere else is offered a reload.
 */
function BuildWatch() {
  const { me } = useAuth()
  const route = usePublicRoute()
  const holdsNothing =
    !me &&
    (route.kind === 'home' ||
      route.kind === 'shuls' ||
      (route.kind === 'org' && route.page === 'zmanim'))
  return <StaleBuild policy={holdsNothing ? 'refresh' : 'offer'} />
}

/*
 * The tab says which product this is, because one bundle serves all of them and
 * `index.html` can only carry one name. Set before the first render so a tab
 * restored from history is never labeled with the wrong product.
 */
{
  const site = currentSite()
  // A congregation's own domain is left alone: its name is the org's, which is
  // not known until its page has loaded one.
  if (site === 'platform') document.title = PLATFORM_NAME
  else if (site !== 'org') document.title = productName(site)

  /*
   * The icon follows the name, and for the same reason: one bundle serves every
   * product and `index.html` can declare only one. It is swapped rather than
   * added, so a tab never carries two icons and never falls back to ShulBase's
   * aron over another product's page.
   */
  if (site !== 'org' && site !== DEFAULT_PRODUCT) {
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (icon) icon.href = `/favicon-${site}.svg`
  }
}

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('Root element #root not found')

/*
 * A lazy chunk that 404s is a deploy that landed under a running tab, and every
 * route here is lazy. Installed before the first render so a navigation made
 * during startup is covered too.
 */
installChunkReload()

/*
 * One root per element, kept across hot reloads.
 *
 * `createRoot` on an element that already has one leaves two Reacts rendering
 * into the same node: the app looks present but its handlers belong to a tree
 * nobody is updating, so it silently stops responding. In dev this module is
 * re-executed on reload, so the root is cached on the element itself.
 */
type RootHolder = HTMLElement & { _root?: ReturnType<typeof createRoot> }
const holder = rootEl as RootHolder
holder._root ??= createRoot(rootEl)

/*
 * The boundary is OUTSIDE the providers, so a crash while a provider is setting
 * itself up still lands on a designed page rather than a blank document. It
 * catches every surface — the back office, the public site and the marketing
 * root — because a white screen is the same dead end whichever one you were on.
 */
holder._root.render(
  <ErrorBoundary>
    <ThemeProvider>
      <Providers>
        <AuthProvider>
          {/* Above every surface, because the act a toast reports usually closes
              the one that raised it. */}
          <ToastProvider>
            <Root />
            <BuildWatch />
          </ToastProvider>
        </AuthProvider>
      </Providers>
    </ThemeProvider>
  </ErrorBoundary>,
)
